// Reevaluaciones del bloque D de 019 (Cuidados post-anestésicos), puras y testeables sin React.
//
// El papel imprime, bajo el gráfico de monitorización, una cuadrícula con las 6 escalas (Aldrete modificado, Bromage, Ramsay, EVA, Steward,
// Otro) por CADA uno de los 6 bloques horarios: son hasta 6 reevaluaciones (una por hora). Antes solo se capturaba una. Cada reevaluación
// lleva su hora (hh:mm, opcional) y sus 6 escalas; se serializa a `Par[]` (respaldo del PDF) y a `Observation` (con `momento=reevaluacion`
// y el componente `numero`, para no pisarse entre sí). El `numero` es la posición en la lista al guardar (1-6), no un bloque horario fijo; la hora tecleada
// va en el componente `hora` del Observation.
import type { Encounter, Observation, Patient, Reference } from '@medplum/fhirtypes';
import { estadoInicialEscalas, observacionesDeEscalas, paresDeEscalas, SISTEMA_ESCALAS_RECUPERACION, type EstadoEscalasRecuperacion } from './escalasRecuperacionAnestesicaDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

/** Bloques horarios de la cuadrícula de escalas del papel de 019. */
export const TOPE_REEVALUACIONES = 6;

export interface Reevaluacion {
  readonly id: string;
  readonly hora: string;
  readonly escalas: EstadoEscalasRecuperacion;
}

export function reevaluacionVacia(): Reevaluacion {
  return { id: crypto.randomUUID(), hora: '', escalas: estadoInicialEscalas() };
}

function conDatos(r: Reevaluacion): boolean {
  return paresDeEscalas((c) => c, 'x', r.escalas).length > 0;
}

/** Un bloque de pares por reevaluación con datos, rotulado "Reevaluación N (hh:mm)"; el N es su posición en la lista (1-6). */
export function paresDeReevaluaciones(t: Traducir, prefijoI18n: string, reevaluaciones: readonly Reevaluacion[]): Par[] {
  return reevaluaciones.flatMap((r, i) => {
    const rotulo = `${t('formularios.f019.bloqueD.escalasTitulo')} ${i + 1}${r.hora.trim() ? ` (${r.hora.trim()})` : ''}`;
    return paresDeEscalas(t, prefijoI18n, r.escalas).map((p) => ({ pregunta: `${rotulo} · ${p.pregunta}`, respuesta: p.respuesta }));
  });
}

export function observacionesDeReevaluaciones(subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, reevaluaciones: readonly Reevaluacion[], efectivo: string): Observation[] {
  return reevaluaciones.flatMap((r, i) => {
    if (!conDatos(r)) return [];
    const hora = r.hora.trim();
    return observacionesDeEscalas(subject, encounter, 'reevaluacion', r.escalas, efectivo, i + 1).map((o) => (hora ? { ...o, component: [...(o.component ?? []), { code: { coding: [{ system: SISTEMA_ESCALAS_RECUPERACION, code: 'hora' }] }, valueString: hora }] } : o));
  });
}
